import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { PropertyValues } from 'lit';
import type { AscBadge } from '../../elements/asc-badge/asc-badge';
import '../../elements/asc-badge/asc-badge';

type BadgeGroupVariant = 'pill' | 'badge';
type BadgeGroupPlacement = 'leading' | 'trailing';
type BadgeGroupSize = 'sm' | 'md' | 'lg';
type BadgeGroupTheme = 'success' | 'gray' | 'brand' | 'error';

/**
 * A composed container that pairs an `asc-badge` with supplementary text and an
 * optional decorative icon.
 *
 * The outer container owns the visual surface (background, border, and pill/badge
 * border-radius). `variant` and `size` are cascaded automatically to the slotted
 * `asc-badge`. `placement` controls whether the badge leads or trails the text by
 * changing the **physical DOM order** — not CSS `order` — so screen reader reading
 * order matches visual layout.
 *
 * Used to annotate status, categorization, or metadata alongside a human-readable
 * descriptor in both marketing and application UIs.
 *
 * > **Note on `variant` naming:** The Figma axis is called `type` with values
 * > `"Pill color"` and `"Badge modern"`. The property is named `variant` to match
 * > the identical axis on `asc-badge` and to avoid conflicting with the reserved
 * > `style` HTML attribute.
 *
 * @element asc-badge-group
 *
 * @slot          - Required. A single `asc-badge` element. `variant` and `size`
 *   are cascaded to it automatically. Non-`asc-badge` children are ignored by
 *   the cascade logic but will still render.
 * @slot text     - Required. Supplementary text label rendered adjacent to the
 *   badge. Accepts plain text or inline phrasing content.
 * @slot visual   - Optional. Decorative icon displayed at the far edge of the
 *   text area. Must carry `aria-hidden="true"` on the slotted element.
 *
 * @csspart base   - Root `<span>` (non-href) or `<a>` (when `href` is set); owns background, border, border-radius, gap, and padding.
 * @csspart visual - Wrapper `<span>` for the `visual` slot. Hidden when the slot is empty.
 * @csspart text   - Wrapper `<span>` for the `text` slot; applies text sizing and color.
 * @csspart badge  - Wrapper `<span>` for the default (`asc-badge`) slot.
 *
 * @cssprop --asc-badge-group-bg                 - Container background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-badge-group-border-color        - Container border color. Default: `var(--asc-border-secondary)`; set per `theme` when `theme` is present (gray → `--asc-border-primary`, brand → `--asc-border-brand`, success → `--asc-border-success`, error → `--asc-border-error`). Consumer override always wins.
 * @cssprop --asc-badge-group-border-radius       - Container border-radius; set internally per `variant`. Consumer override takes precedence.
 * @cssprop --asc-badge-group-gap                - Flex gap between badge, text, and visual parts. Default: `var(--asc-spacing-md)`; sm: `var(--asc-spacing-sm)`
 * @cssprop --asc-badge-group-padding-y           - Block (vertical) padding. Default: `var(--asc-spacing-xs)`; sm: `var(--asc-spacing-xxs)`
 * @cssprop --asc-badge-group-padding-badge-side  - Inline padding on the badge-facing edge (tight, hugs the badge). Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-badge-group-padding-text-side   - Inline padding on the text/content-facing edge (spacious). Default: `var(--asc-spacing-md)` (leading) / `var(--asc-spacing-lg)` (trailing)
 * @cssprop --asc-badge-group-text-color          - Text color for the `text` slot. Default: `var(--asc-accent-teal-text-primary)` (pill) / `var(--asc-text-secondary)` (badge)
 * @cssprop --asc-badge-group-text-font-size      - Font size for the `text` slot. Default: `var(--asc-font-size-text-xs)` (sm/md) / `var(--asc-font-size-text-sm)` (lg)
 * @cssprop --asc-badge-group-text-line-height    - Line height for the `text` slot. Default: `var(--asc-line-height-text-xs)` (sm/md) / `var(--asc-line-height-text-sm)` (lg)
 * @cssprop --asc-badge-group-text-font-weight    - Font weight for the `text` slot. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-badge-group-icon-size           - Width/height applied to slotted visual icon. Default: `var(--asc-spacing-xl)` (md/lg) / `var(--asc-spacing-lg)` (sm)
 * @cssprop --asc-badge-group-bg-hover             - Background color applied on hover when `href` is set. Default: `var(--asc-bg-primary_hover)` (pill) / `var(--asc-bg-secondary)` (badge)
 */
@customElement('asc-badge-group')
export class AscBadgeGroup extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
    }

    [hidden] {
      display: none !important;
    }

    /* ── Base container ─────────────────────────────────────────────────────── */
    .base {
      display: inline-flex;
      align-items: center;
      gap: var(--asc-badge-group-gap, var(--asc-spacing-md));
      background: var(--asc-badge-group-bg, var(--asc-bg-primary));
      border: var(--asc-border-width) solid
        var(--asc-badge-group-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-badge-group-border-radius, var(--asc-radius-full));
      padding-block: var(--asc-badge-group-padding-y, var(--asc-spacing-xs));
    }

    /* ── Placement ──────────────────────────────────────────────────────────── */
    /* Leading (default): render order is [badge][text][visual].
       Badge is on the LEFT → tight padding on the start, spacious on the end. */
    :host([placement='leading']) .base {
      padding-inline-start: var(
        --asc-badge-group-padding-badge-side,
        var(--asc-spacing-xs)
      );
      padding-inline-end: var(
        --asc-badge-group-padding-text-side,
        var(--asc-spacing-md)
      );
    }

    /* Trailing: render order is [visual][text][badge].
       Badge is on the RIGHT → spacious padding on the start, tight on the end. */
    :host([placement='trailing']) .base {
      padding-inline-start: var(
        --asc-badge-group-padding-text-side,
        var(--asc-spacing-lg)
      );
      padding-inline-end: var(
        --asc-badge-group-padding-badge-side,
        var(--asc-spacing-xs)
      );
    }

    /* ── Variant ────────────────────────────────────────────────────────────── */
    /* Pill: fully rounded, no shadow. Confirmed from Figma: radius-full. */
    :host([variant='pill']) .base {
      border-radius: var(--asc-badge-group-border-radius, var(--asc-radius-full));
    }

    /* Badge (modern): lightly rounded with a subtle drop shadow.
       Confirmed from Figma: radius-sm (8px), shadow-xs. */
    :host([variant='badge']) .base {
      border-radius: var(--asc-badge-group-border-radius, var(--asc-radius-sm));
      box-shadow: 0 1px 2px 0 var(--asc-shadow-xs);
    }

    /* ── Theme ──────────────────────────────────────────────────────────────── */
    /* Theme affects ONLY the container's outline/border color — never the
       background. Border colors mirror the per-theme border tokens used by
       asc-badge so the group outline matches the badge it wraps. A consumer
       override via --asc-badge-group-border-color always takes precedence. */
    :host([theme='gray']) .base {
      border-color: var(--asc-badge-group-border-color, var(--asc-border-primary));
    }

    :host([theme='brand']) .base {
      border-color: var(--asc-badge-group-border-color, var(--asc-border-brand));
    }

    :host([theme='success']) .base {
      border-color: var(--asc-badge-group-border-color, var(--asc-border-success));
    }

    :host([theme='error']) .base {
      border-color: var(--asc-badge-group-border-color, var(--asc-border-error));
    }

    /* ── Sizes ──────────────────────────────────────────────────────────────── */
    /* sm — extrapolated; not present in the Figma source design */
    :host([size='sm']) .base {
      gap: var(--asc-badge-group-gap, var(--asc-spacing-sm));
      padding-block: var(--asc-badge-group-padding-y, var(--asc-spacing-xxs));
    }

    /* sm+leading: badge on left → keep start tight (inherited), override end to sm text-side */
    :host([size='sm'][placement='leading']) .base {
      padding-inline-end: var(
        --asc-badge-group-padding-text-side,
        var(--asc-spacing-sm)
      );
    }

    /* sm+trailing: badge on right → keep end tight (inherited), override start to sm text-side */
    :host([size='sm'][placement='trailing']) .base {
      padding-inline-start: var(
        --asc-badge-group-padding-text-side,
        var(--asc-spacing-sm)
      );
    }

    /* Special case: badge + md + trailing — text-side start needs 10px (between spacing-md=8px
       and spacing-lg=12px). Selector beats the generic trailing rule above. */
    :host([variant='badge'][size='md'][placement='trailing']) .base {
      padding-inline-start: 10px; /* no-token: Figma badge+md text-side; spacing-md=8px, spacing-lg=12px */
    }

    /* ── Text wrapper ───────────────────────────────────────────────────────── */
    /* Styles on .text cascade to slotted text content via CSS inheritance. */
    .text {
      font-family: var(--asc-font-family-body);
      font-weight: var(
        --asc-badge-group-text-font-weight,
        var(--asc-font-weight-medium)
      );
      /* sm/md default — lg overridden below */
      font-size: var(
        --asc-badge-group-text-font-size,
        var(--asc-font-size-text-xs)
      );
      line-height: var(
        --asc-badge-group-text-line-height,
        var(--asc-line-height-text-xs)
      );
      /* Pill variant: teal accent text. Confirmed from Figma: var(--teal/text-primary) → --asc-accent-teal-text-primary */
      color: var(
        --asc-badge-group-text-color,
        var(--asc-accent-teal-text-primary)
      );
      white-space: nowrap;
    }

    /* lg text scale — confirmed from Figma: Text sm/Medium (14px/20px) */
    :host([size='lg']) .text {
      font-size: var(
        --asc-badge-group-text-font-size,
        var(--asc-font-size-text-sm)
      );
      line-height: var(
        --asc-badge-group-text-line-height,
        var(--asc-line-height-text-sm)
      );
    }

    /* Badge variant: secondary text color. Confirmed from Figma: var(--colors/text/text-secondary) → --asc-text-secondary */
    :host([variant='badge']) .text {
      color: var(--asc-badge-group-text-color, var(--asc-text-secondary));
    }

    /* ── Visual (icon) wrapper ──────────────────────────────────────────────── */
    .visual {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    /* md/lg icon size: 16px confirmed from Figma (size-[16px] on icon wrapper).
       No Figma variable binding on this property → use nearest token --asc-spacing-xl (16px). */
    .visual ::slotted(*) {
      display: block;
      width: var(--asc-badge-group-icon-size, var(--asc-spacing-xl));
      height: var(--asc-badge-group-icon-size, var(--asc-spacing-xl));
      flex-shrink: 0;
    }

    /* sm icon size — extrapolated to spacing-lg (12px) */
    :host([size='sm']) .visual ::slotted(*) {
      width: var(--asc-badge-group-icon-size, var(--asc-spacing-lg));
      height: var(--asc-badge-group-icon-size, var(--asc-spacing-lg));
    }

    /* ── Badge wrapper ──────────────────────────────────────────────────────── */
    .badge {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }

    /* ── Href state ─────────────────────────────────────────────────────────── */
    /* When href is set the base element is rendered as <a>. Reset browser defaults. */
    a.base {
      text-decoration: none;
      color: inherit;
    }

    /* In link mode the slotted badge/text/visual are presentational. Let pointer
       events fall through to the <a> so the whole surface is one uniform click
       target with the link cursor — otherwise the slotted asc-badge's own
       cursor:default and click target carve a dead zone out of the link. */
    a.base ::slotted(*) {
      pointer-events: none;
    }

    /* Pill hover: bg-primary_hover. Confirmed from Figma:
       var(--colors/background/bg-primary_hover) → --asc-bg-primary_hover */
    a.base:hover {
      background: var(--asc-badge-group-bg-hover, var(--asc-bg-primary_hover));
      cursor: pointer;
    }

    /* Badge modern hover: bg-secondary. Confirmed from Figma:
       var(--colors/background/bg-secondary) → --asc-bg-secondary */
    :host([variant='badge']) a.base:hover {
      background: var(--asc-badge-group-bg-hover, var(--asc-bg-secondary));
    }

    /* Focus ring for keyboard navigation when the component acts as a link */
    a.base:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }
  `;

  /**
   * Controls the outer container's border-radius shape and is cascaded to the
   * slotted `asc-badge`. `'pill'` is fully rounded (Figma: "Pill color");
   * `'badge'` is lightly rounded with a drop shadow (Figma: "Badge modern").
   */
  @property({ type: String, reflect: true }) variant: BadgeGroupVariant = 'pill';

  /**
   * Controls the physical DOM render order of the visual, text, and badge slot
   * wrappers. `'leading'` renders the badge first (on the left);
   * `'trailing'` renders the badge last (on the right).
   */
  @property({ type: String, reflect: true }) placement: BadgeGroupPlacement = 'leading';

  /**
   * Controls outer-container sizing (padding, gap, text scale, icon size) and is
   * cascaded to the slotted `asc-badge`.
   */
  @property({ type: String, reflect: true }) size: BadgeGroupSize = 'md';

  /**
   * The color theme applied to the container. Offers the same options as
   * `asc-badge` (`'gray' | 'brand' | 'success' | 'error'`) but, unlike the
   * badge, it affects **only the container's outline/border color** — never the
   * background. Leave unset (the default) to keep the neutral
   * `--asc-border-secondary` outline.
   *
   * The theme is also cascaded to the slotted `asc-badge`, but only when that
   * badge does not declare its own `theme`. An explicit `theme` on the badge
   * always wins over the group's.
   */
  @property({ type: String, reflect: true }) theme?: BadgeGroupTheme;

  /**
   * When provided, the entire badge group is wrapped in a native `<a>` element,
   * making it a keyboard-accessible, focusable link. Hover states are applied
   * automatically per the Figma spec. Leave empty (the default) for a purely
   * presentational, non-interactive container.
   *
   * > **SEO note:** The `<a>` renders inside Shadow DOM. Googlebot follows
   * > Shadow DOM anchors; other crawlers may not. For maximum compatibility,
   * > wrap the entire `<asc-badge-group>` in a light DOM `<a>` instead.
   */
  @property({ type: String }) href?: string;

  /** Forwarded to `<a target>`. Only meaningful when `href` is set. */
  @property({ type: String }) target?: string;

  /**
   * Forwarded to `<a rel>`. Only meaningful when `href` is set.
   * When `target="_blank"` and no `rel` is provided, `"noopener noreferrer"`
   * is injected automatically to prevent tab-nabbing (OWASP A05).
   */
  @property({ type: String }) rel?: string;

  @state() private _hasVisual = false;

  /**
   * Badges whose `theme` this group has set via the cascade. Tracked so that a
   * later cascade (e.g. the group's `theme` changing) is not mistaken for an
   * author-provided theme and skipped — see {@link _syncBadge}.
   */
  private readonly _cascadedTheme = new WeakSet<AscBadge>();

  override updated(changed: PropertyValues): void {
    if (changed.has('variant') || changed.has('size') || changed.has('theme')) {
      this._syncBadge();
    }
  }

  /**
   * Finds the first slotted `asc-badge` and syncs `variant` and `size` onto it.
   * The group's `theme` is cascaded too, but only when the badge does not
   * declare its own `theme` (an explicit badge theme always wins).
   * Emits a console warning if slot content exists but no `asc-badge` is found.
   */
  private _syncBadge(): void {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])');
    if (!slot) return;

    const elements = slot.assignedElements({ flatten: true });
    const badge = elements.find(
      (el) => el.tagName === 'ASC-BADGE'
    ) as AscBadge | undefined;

    if (!badge) {
      if (elements.length > 0) {
        console.warn(
          '[asc-badge-group] Default slot must contain at least one asc-badge element. Other slotted children will be ignored by the cascade.'
        );
      }
      return;
    }

    // Fall back to the declared defaults when the attribute has been removed or
    // set to an empty string (|| catches null, undefined, and '').
    badge.variant = this.variant || 'pill';
    badge.size = this.size || 'md';

    // Theme cascade — the group's theme fills in a badge that has no theme of
    // its own; an explicit theme on the badge always wins.
    //
    // Detecting "no theme of its own" must be immune to asc-badge reflecting
    // its DEFAULT theme: a badge authored without `theme` still ends up with a
    // reflected `theme="gray"` attribute. So we treat the neutral `gray`
    // default as "unthemed" and only an explicit non-gray value as author
    // intent. Badges this group has already themed are tracked in
    // `_cascadedTheme` so that a later cascade (the group's theme changing)
    // isn't mistaken for author intent and skipped.
    const badgeTheme = badge.getAttribute('theme');
    const badgeHasAuthorTheme =
      badgeTheme !== null &&
      badgeTheme !== 'gray' &&
      !this._cascadedTheme.has(badge);

    if (this.theme && !badgeHasAuthorTheme) {
      this._cascadedTheme.add(badge);
      badge.theme = this.theme;
    }
  }

  private _onBadgeSlotChange(): void {
    this._syncBadge();
  }

  private _onVisualSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasVisual = slot.assignedNodes({ flatten: true }).length > 0;
  }

  override render() {
    const visualPart = html`
      <span part="visual" class="visual" ?hidden=${!this._hasVisual}>
        <slot name="visual" @slotchange=${this._onVisualSlotChange}></slot>
      </span>
    `;

    const textPart = html`
      <span part="text" class="text">
        <slot name="text"></slot>
      </span>
    `;

    const badgePart = html`
      <span part="badge" class="badge">
        <slot @slotchange=${this._onBadgeSlotChange}></slot>
      </span>
    `;

    const inner =
      this.placement === 'leading'
        ? html`${badgePart}${textPart}${visualPart}`
        : html`${visualPart}${textPart}${badgePart}`;

    // Security: auto-inject rel="noopener noreferrer" on _blank anchors when no
    // explicit rel is provided, preventing tab-nabbing (OWASP A05).
    const safeRel =
      this.target === '_blank' && !this.rel ? 'noopener noreferrer' : this.rel;

    return this.href
      ? html`<a part="base" class="base" href=${this.href} target=${ifDefined(this.target)} rel=${ifDefined(safeRel)}>${inner}</a>`
      : html`<span part="base" class="base">${inner}</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-badge-group': AscBadgeGroup;
  }
}
